import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { ASSETS_NAMES, prefix } from './utils/form';
import { EquipmentItem } from '../Equipment/EquipmentItem';
import { equipmentSectionInitialValues } from '../Equipment/utils/form';

export const OtherAssets: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.BUSINESS_OPERATIONS,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.ASSETS,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('businessOperations.title')}
          badgeText={t('businessOperations.assets.title')}
          section={FOR_BANK_SECTIONS.BUSINESS_OPERATIONS}
        />
      </FormSectionCard>
      {/* 6.3.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.assets.description')}
        aiFieldName={concatWithDot(prefix, ASSETS_NAMES.DESCRIPTION)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, ASSETS_NAMES.DESCRIPTION)}
          name={concatWithDot(prefix, ASSETS_NAMES.DESCRIPTION)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.3.2 */}
      <FormSectionCard disabled={!canAccess} title={t('businessOperations.assets.group.title')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(prefix, ASSETS_NAMES.GROUPS)}
          initialItem={equipmentSectionInitialValues}
          buttonText={t('businessOperations.assets.group.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <EquipmentItem isCurrentEquipment name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 6.3.3 */}
      <FormSectionCard disabled={!canAccess} title={t('businessOperations.assets.purchasedGroup.title')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(prefix, ASSETS_NAMES.PURCHASED_GROUPS)}
          initialItem={equipmentSectionInitialValues}
          buttonText={t('businessOperations.assets.purchasedGroup.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <EquipmentItem name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 6.3.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.assets.rationaleForPurchasing')}
        aiFieldName={concatWithDot(prefix, ASSETS_NAMES.RATIONALE_FOR_PURCHASING)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, ASSETS_NAMES.RATIONALE_FOR_PURCHASING)}
          name={concatWithDot(prefix, ASSETS_NAMES.RATIONALE_FOR_PURCHASING)}
          minRows={4}
          maxLength={1500}
          disabled={!canAccess}
        />
      </FormSectionCard>
    </>
  );
};
